<template>
    <section class="current-operations">
        <div class="current-toolbar">
            <div class="current-heading">
                <h4 class="current-title">{{ t('Current') }}</h4>
                <p class="current-description">{{ t('Description') }}</p>
            </div>
            <div class="current-actions">
                <b-button class="btn-title-action" :disabled="loading" @click="load">
                    <feather-icon icon="RefreshCwIcon" size="14" />
                    {{ t('Refresh') }}
                </b-button>
                <b-button v-b-toggle.sidebar-current-table class="btn-title-action" :disabled="loading">
                    {{ $t('ProductionList.ManageTable') }}
                </b-button>
                <b-button class="btn-title-action" :disabled="loading || exporting || !total" @click="exportCsv">
                    <feather-icon icon="DownloadIcon" size="14" />
                    {{ exporting ? t('Loading') : $t('ProductionList.ExportCSV') }}
                </b-button>
            </div>
        </div>
        <div class="current-filters">
            <b-form-group :label="t('Search')" label-for="current-search" class="filter-search mb-0">
                <b-form-input id="current-search" v-model="search" :placeholder="t('SearchHint')" @input="scheduleSearch" @keyup.enter="applyFilters" />
            </b-form-group>
            <b-form-group :label="t('ResourceGroup')" label-for="current-workcenters" class="filter-select mb-0">
                <v-select input-id="current-workcenters" v-model="workcenterIds" :options="workcenters" label="workcenterName" :reduce="item => item.workcenterId" multiple :close-on-select="false" :deselect-from-dropdown="true" :placeholder="$t('Select')" @input="applyFilters">
                    <template #selected-option="option"><span class="filter-selected-label">{{ option.workcenterName }}</span></template>
                    <template #open-indicator><span class="filter-select-arrow"><DropdownIcon /></span></template>
                </v-select>
            </b-form-group>
            <b-form-group :label="t('Resources')" label-for="current-resources" class="filter-select mb-0">
                <v-select input-id="current-resources" v-model="resourceIds" :options="resources" label="name" :reduce="item => item.id" multiple :close-on-select="false" :deselect-from-dropdown="true" :placeholder="$t('Select')" @input="applyFilters">
                    <template #selected-option="option"><span class="filter-selected-label">{{ option.name }}</span></template>
                    <template #open-indicator><span class="filter-select-arrow"><DropdownIcon /></span></template>
                </v-select>
            </b-form-group>
            <periodFilter class="filter-period" picker-id="current-period" :options="periodOptions" position="right" :maxRangeDates="60" @change="changePeriod" />
            <b-button class="btn-title-action btn-filled filter-submit" :disabled="loading" @click="applyFilters">{{ t('Filter') }}</b-button>
        </div>
        <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>
        <b-table :items="rows" :fields="fields" :busy="loading" responsive show-empty primary-key="operationId"
            :empty-text="t('Empty')" class="operation-summary table-production-progress" @row-clicked="toggleHistory" @sort-changed="sortChanged" no-local-sorting>
            <template #table-busy><div class="text-center p-3"><b-spinner small /> {{ t('Loading') }}</div></template>
            <template #cell()="{ value }"><span class="summary-value" :title="String(value || '')">{{ value || '—' }}</span></template>
            <template #cell(expand)="row">
                <button type="button" class="btn-expand" :class="{ 'is-open': row.detailsShowing }"
                    :title="t(row.detailsShowing ? 'Collapse' : 'Expand')"
                    :aria-label="t(row.detailsShowing ? 'Collapse' : 'Expand')"
                    :aria-expanded="String(row.detailsShowing)" @click.stop="toggleHistory(row.item)">
                    <feather-icon :icon="row.detailsShowing ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="16" />
                    {{ t(row.detailsShowing ? 'HideHistory' : 'ViewHistory') }}
                </button>
            </template>
            <template #cell(user)="{ item }"><span class="summary-value" :title="userLabel(item)">{{ userLabel(item) }}</span></template>
            <template #cell(statusName)="{ item }">
                <div v-if="item.statusName" class="badge badge-status-custom" :style="{ backgroundColor: item.statusColor }" :title="item.statusName">
                    <div class="add-ellipsis-production-p-pcp"><span>{{ item.statusName }}</span></div>
                </div>
                <span v-else>—</span>
            </template>
            <template #cell(eventTime)="{ item }">{{ date(item.eventTime) }}</template>
            <template #cell(durationInSeconds)="{ item }">{{ item.isCompleted ? t('Closed') : duration(item.durationInSeconds) }}</template>
            <template #cell(operationElapsedSeconds)="{ item }"><span :title="t('OperationElapsedHint')">{{ item.operationElapsedSeconds == null ? '—' : duration(item.operationElapsedSeconds) }}</span></template>
            <template #row-details="{ item }">
                <div class="history-panel">
                    <div class="d-flex flex-wrap justify-content-between mb-1">
                        <strong>{{ t('History') }} · {{ item.orderNumber }} / {{ item.number }}</strong>
                    </div>
                    <p class="text-muted mb-1">{{ t('HistoryHint') }}</p>
                    <div v-if="history[item.operationId].loading" class="p-2"><b-spinner small /> {{ t('Loading') }}</div>
                    <b-alert :show="!!history[item.operationId].error" variant="danger">
                        {{ history[item.operationId].error }}
                        <b-button size="sm" class="btn-title-action" @click="loadHistory(item.operationId)">{{ t('Refresh') }}</b-button>
                    </b-alert>
                    <b-table v-if="!history[item.operationId].loading && !history[item.operationId].error" :items="history[item.operationId].rows" :fields="historyFields" responsive show-empty :empty-text="t('Empty')" class="operation-summary history-table">
                        <template #cell(eventTime)="{ item: event }">{{ date(event.eventTime) }}</template>
                        <template #cell(nextEventTime)="{ item: event }">{{ event.isCompleted ? '—' : event.nextEventTime ? date(event.nextEventTime) : t('Open') }}</template>
                        <template #cell(user)="{ item: event }">{{ userLabel(event) }}</template>
                        <template #cell(statusName)="{ item: event }">
                            <span class="status-dot" :style="{ backgroundColor: event.statusColor }" />{{ event.statusName }}
                            <small v-if="!event.nextEventTime" class="d-block text-muted">{{ t('Latest') }}</small>
                        </template>
                        <template #cell(durationInSeconds)="{ item: event }">
                            <span :title="event.durationInSeconds === 0 ? t('SameTimeHint') : ''">{{ eventDuration(event) }}</span>
                        </template>
                    </b-table>
                    <b-pagination v-if="history[item.operationId].total > 10" v-model="history[item.operationId].page" align="right"
                        :total-rows="history[item.operationId].total" :per-page="10" class="mt-1 mb-0"
                        first-number last-number prev-class="prev-item" next-class="next-item" @input="loadHistory(item.operationId)">
                        <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
                        <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
                    </b-pagination>
                </div>
            </template>
        </b-table>
        <div class="col-12 col-footer-table pagination-table">
            <div class="d-flex align-items-center mb-0 mt-1 por-pagina">
                <span class="text-nowrap">{{ total }} {{ t('Operations') }}</span>
                <b-form-select v-model="pageSize" :options="[10, 25, 50]" :aria-label="t('PageSize')" class="mx-1" @change="applyFilters" />
            </div>
            <b-pagination v-model="page" :total-rows="total" :per-page="pageSize" class="mt-1 mb-0"
                first-number last-number prev-class="prev-item" next-class="next-item" @input="load">
                <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
                <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
            </b-pagination>
        </div>
        <small v-if="loadedAt" class="d-block text-muted text-right mt-1">{{ t('Updated') }} {{ loadedAt }}</small>
        <b-sidebar id="sidebar-current-table" v-model="sidebarVisible" class="sidebar-manage-table" shadow right
            bg-variant="white" width="407px" backdrop @hidden="resetColumns">
            <template #header>
                <div class="p-2 d-flex justify-content-between">
                    <span class="sidebar-title">{{ $t('ProductionList.ManageTable') }}</span>
                    <span v-b-toggle.sidebar-current-table class="close-icon"><close-icon /></span>
                </div>
                <div class="separator" />
                <span class="d-flex info-sidebar">{{ $t('ProductionList.infoColumn') }}</span>
            </template>
            <b-col cols="12" class="p-0 itens-drag">
                <draggable v-if="reRenderSelects" tag="ul" :list="listConfig" class="list-group" handle=".handle" v-bind="dragOptions">
                    <transition-group>
                        <li v-for="(item, index) in listConfig" :key="`${item.name}-${index}`" @click="openSelect(index)">
                            <div class="list-group-item">
                                <DragIcon class="handle" />
                                <div class="col-input-icon">
                                    <SingleSelect v-if="item.selectVisible" :key="`${item.name}-${index}`" :placeholder="$t('Select')"
                                        :option-selected="columnOptions.find(f => f.value == item.name)" :options="columnOptionsHelper"
                                        :translate="true" :disabled="!item.removed" :state="!item.name && submitted ? false : null"
                                        @input="(value) => { item.name = value ? value.value : value; updateOptions(); }">
                                        {{ $t('RequiredField') }}
                                    </SingleSelect>
                                    <div v-else class="name-column w-100" :class="{ disabled: !item.removed }">{{ $t(columnText(item)) }}</div>
                                    <div v-if="!item.removed" :id="`tooltip-current-info-${index}`" class="icon-action"><InfoIcon /></div>
                                    <div v-else :id="`tooltip-current-delete-${index}`" class="icon-action trash" @click.stop="listConfig.splice(index, 1)"><DeleteIcon /></div>
                                    <b-tooltip :target="`tooltip-current-info-${index}`" triggers="hover" placement="top">{{ $t('ProductionList.CannotRemove') }}</b-tooltip>
                                    <b-tooltip :target="`tooltip-current-delete-${index}`" triggers="hover" placement="top">{{ $t('ProductionList.RemoveColumn') }}</b-tooltip>
                                </div>
                            </div>
                        </li>
                    </transition-group>
                </draggable>
                <b-button class="btn-add-collumn btn-footer" :disabled="listConfig.length >= columnOptions.length" @click="addColumn">
                    <AddCircleIcon class="add-circle-icon" />
                    <span>{{ $t('ProductionList.AddColumn') }}</span>
                </b-button>
            </b-col>
            <template #footer>
                <b-col md="12" class="footer-sidebar">
                    <b-button class="btn-reset btn-footer" @click="resetColumns">
                        <span class="btn-reset-text">{{ $t('ProductionList.RestoreConfig') }}</span>
                        <RestoreIcon class="restore-icon" />
                    </b-button>
                    <div class="btn-footer-left">
                        <b-button v-b-toggle.sidebar-current-table class="btn-cancel btn-footer">{{ $t('RefuseStatus.Cancel') }}</b-button>
                        <b-button class="btn-save btn-footer" @click="saveColumns">{{ $t('RefuseStatus.Save') }}</b-button>
                    </div>
                </b-col>
            </template>
        </b-sidebar>
    </section>
</template>

<script>
import { BAlert, BButton, BCol, BFormGroup, BFormInput, BFormSelect, BPagination, BSidebar, BSpinner, BTable, BTooltip, VBToggle } from 'bootstrap-vue';
import vSelect from 'vue-select';
import draggable from 'vuedraggable';
import moment from 'moment';
import periodFilter from '@/components/components-code/period-filter-component';
import { formatDurationHms } from '@/views/pages/operator/utils';

const lockedColumns = ['orderNumber', 'number', 'statusName', 'eventTime', 'durationInSeconds', 'operationElapsedSeconds', 'workcenterName', 'resourceName'];

export default {
    components: {
        vSelect, periodFilter, draggable, BAlert, BButton, BCol, BFormGroup, BFormInput, BFormSelect, BPagination, BSidebar, BSpinner, BTable, BTooltip,
        SingleSelect: () => import('@core/components/multiselect/SingleSelect.vue'),
        DragIcon: () => import('@/assets/images/pages/drag-icon.svg'),
        DeleteIcon: () => import('@/assets/images/pages/delete-trash-icon.svg'),
        InfoIcon: () => import('@/assets/images/pages/info-outline-icon.svg'),
        CloseIcon: () => import('@/assets/images/icons/close-icon.svg'),
        AddCircleIcon: () => import('@/assets/images/pages/plus-circle-icon.svg'),
        RestoreIcon: () => import('@/assets/images/icons/restore-icon.svg'),
        DropdownIcon: () => import('@/components/components-code/period-filter-component/icons/arrow-down-orange.svg')
    },
    directives: { 'b-toggle': VBToggle },
    props: { siteId: { required: true }, operationId: { default: null }, userNames: { type: Object, default: () => ({}) } },
    data() {
        return {
            rows: [], history: {}, total: 0, page: 1, pageSize: 10, search: '', searchTimer: null, startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'), endDate: moment().format('YYYY-MM-DD'),
            workcenterIds: [], resourceIds: [], workcenters: [], resources: [],
            periodOptions: [
                ...[7, 30, 60].map(days => ({ value: days, text: `PeriodFilterComponent.${days}Days` })),
                { value: 3000, text: 'PeriodFilterComponent.AllPeriod' },
                { value: 0, text: 'PeriodFilterComponent.Custom' }
            ],
            applied: {}, sort: 'desc', fieldSort: '', loading: false, exporting: false, error: '', loadedAt: '', requestId: 0,
            appliedColumns: lockedColumns.map(name => ({ name, removed: false })),
            listConfig: [], columnOptionsHelper: [], sidebarVisible: false, reRenderSelects: true, submitted: false,
            dragOptions: { animation: 200, group: 'current-columns', disabled: false, ghostClass: 'ghost' }
        };
    },
    computed: {
        storageKey() { return `mysfc-current-columns-${this.$cookies.get('userInfo')?.userData?.guid}-${this.siteId}`; },
        columnOptions() {
            return [['orderNumber', 'Order'], ['number', 'Operation'], ['name', 'Name'], ['statusName', 'CurrentStatus'],
                ['eventTime', 'LastEvent'], ['durationInSeconds', 'SinceLast'], ['operationElapsedSeconds', 'OperationElapsed'], ['workcenterName', 'ResourceGroup'], ['resourceName', 'LastResource'], ['user', 'LastUser']]
                .map(([value, text]) => ({ value, text: `ProductionViews.${text}` }));
        },
        availableFields() {
            return this.columnOptions.map(({ value, text }) => ({ key: value, label: this.$t(text), thStyle: { minWidth: value === 'number' ? '85px' : '155px', maxWidth: '190px' }, sortable: ['orderNumber', 'number', 'name', 'statusName', 'resourceName'].includes(value) }));
        },
        fields() {
            const byKey = Object.fromEntries(this.availableFields.map(field => [field.key, field]));
            return [{ key: 'expand', label: this.t('HistoryColumn'), thStyle: { width: '168px' } }, ...this.appliedColumns.map(column => byKey[column.name]).filter(Boolean)];
        },
        historyFields() {
            return [['eventTime', 'Start'], ['nextEventTime', 'End'], ['statusName', 'RecordedStatus'], ['durationInSeconds', 'Duration'],
                ['producedQuantity', 'Produced'], ['refuse', 'Refuse'], ['rework', 'Rework'], ['resourceName', 'Resource'], ['user', 'User']]
                .map(([key, label]) => ({ key, label: this.t(label) }));
        }
    },
    watch: {
        siteId() { this.workcenterIds = []; this.resourceIds = []; this.loadFilterOptions(); this.applyFilters(); },
        operationId() { this.page = 1; this.load(); },
        listConfig: { handler() { this.updateOptions(); }, deep: true }
    },
    mounted() {
        this.appliedColumns = this.readColumns();
        this.listConfig = this.clone(this.appliedColumns);
        this.loadFilterOptions();
        this.applyFilters();
    },
    beforeDestroy() { clearTimeout(this.searchTimer); },
    methods: {
        async loadFilterOptions() {
            const siteId = this.siteId;
            this.workcenters = []; this.resources = [];
            if (!siteId) return;
            try {
                const [workcenters, resources] = await Promise.all([
                    this.$http2.get('/api/myconfig/workcenters', { params: { siteId } }),
                    this.$http2.get('/api/myconfig/workcenters/resources', { params: { siteId } })
                ]);
                if (siteId !== this.siteId) return;
                this.workcenters = workcenters.data.data;
                this.resources = resources.data.data;
            } catch (_) { this.error = this.t('Error'); }
        },
        scheduleSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.applyFilters(), 300);
        },
        t(key) { return this.$t(`ProductionViews.${key}`); },
        userLabel(item) {
            const name = [item?.firstName, item?.lastName].filter(Boolean).join(' ').trim();
            const login = String(item?.user || '').trim().toLowerCase();
            return name || this.userNames[login] || item?.user || '—';
        },
        clone(value) { return JSON.parse(JSON.stringify(value)); },
        readColumns() {
            try {
                const saved = JSON.parse(localStorage.getItem(this.storageKey));
                const columns = Array.isArray(saved) ? saved.map(item => typeof item === 'string' ? { name: item, removed: !lockedColumns.includes(item) } : item) : [];
                const valid = columns.filter(item => this.columnOptions.some(option => option.value === item.name));
                if (valid.length) {
                    if (!valid.some(item => item.name === 'operationElapsedSeconds')) valid.splice(valid.findIndex(item => item.name === 'durationInSeconds') + 1, 0, { name: 'operationElapsedSeconds', removed: false });
                    if (!valid.some(item => item.name === 'workcenterName')) valid.splice(valid.findIndex(item => item.name === 'resourceName'), 0, { name: 'workcenterName', removed: false });
                    return valid;
                }
            } catch (_) { /* Keep the default columns when storage is unavailable. */ }
            return lockedColumns.map(name => ({ name, removed: false }));
        },
        resetColumns() { this.listConfig = this.clone(this.appliedColumns); this.submitted = false; },
        columnText(item) { return this.columnOptions.find(option => option.value === item.name)?.text || ''; },
        updateOptions() {
            const selected = new Set(this.listConfig.map(item => item.name));
            this.columnOptionsHelper = this.columnOptions.filter(option => !selected.has(option.value));
        },
        openSelect(index) {
            if (this.listConfig[index].selectVisible) return;
            this.listConfig.forEach(item => { item.selectVisible = false; });
            this.$set(this.listConfig[index], 'selectVisible', true);
            this.reRenderSelects = false;
            this.$nextTick(() => { this.reRenderSelects = true; });
        },
        addColumn() { this.submitted = false; this.listConfig.push({ name: '', removed: true, selectVisible: true }); },
        saveColumns() {
            this.submitted = true;
            if (this.listConfig.some(item => !item.name)) return;
            this.listConfig.forEach(item => { item.selectVisible = false; });
            this.appliedColumns = this.clone(this.listConfig);
            localStorage.setItem(this.storageKey, JSON.stringify(this.appliedColumns));
            this.sidebarVisible = false;
        },
        date(value) { return value ? moment(value).format(this.$i18n.locale === 'en' ? 'MM/DD/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm:ss') : '—'; },
        duration(value) { return formatDurationHms(value, 'seconds'); },
        eventDuration(item) { return item.isCompleted ? '—' : item.durationInSeconds === 0 && item.nextEventTime === item.eventTime ? this.t('SameTime') : this.duration(item.durationInSeconds); },
        changePeriod({ option, datepicker }) {
            this.endDate = option === 3000 ? '' : moment(option === null ? datepicker[1] : undefined).format('YYYY-MM-DD');
            this.startDate = option === 3000 ? '' : option === null
                ? moment(datepicker[0]).format('YYYY-MM-DD')
                : moment(this.endDate).subtract(option - 1, 'days').format('YYYY-MM-DD');
            this.applyFilters();
        },
        applyFilters() {
            clearTimeout(this.searchTimer);
            if (this.startDate && this.endDate && this.startDate > this.endDate) { this.error = this.t('InvalidPeriod'); return; }
            this.applied = { textFilter: this.search, workcenterIds: [...this.workcenterIds], resourceIds: [...this.resourceIds], startDate: this.startDate || undefined, endDate: this.endDate || undefined };
            this.page = 1;
            this.load();
        },
        params(page = this.page, pageSize = this.pageSize) {
            return { siteId: this.siteId, operationId: this.operationId || undefined, pageNumber: page, pageSize,
                allPeriod: !(this.applied.startDate || this.applied.endDate), ...this.applied,
                workcenterIds: undefined, resourceIds: undefined,
                ...Object.fromEntries((this.applied.workcenterIds || []).map((id, i) => [`workcenterIds[${i}]`, id])),
                ...Object.fromEntries((this.applied.resourceIds || []).map((id, i) => [`resourceIds[${i}]`, id])), sort: this.sort, fieldSort: this.fieldSort };
        },
        async load() {
            const requestId = ++this.requestId;
            this.loading = true; this.error = '';
            this.rows.forEach(row => this.$set(row, '_showDetails', false));
            this.history = {};
            try {
                const { data } = await this.$http2.get('/api/mysfc/operation-progress/current', { params: { ...this.params(), includeOperationElapsed: true } });
                if (requestId !== this.requestId) return;
                this.rows = data.data.operationsProgress; this.total = data.data.totalItems;
                this.loadedAt = moment().format('HH:mm:ss');
            } catch (_) { if (requestId === this.requestId) { this.rows = []; this.total = 0; this.error = this.t('Error'); } }
            finally { if (requestId === this.requestId) this.loading = false; }
        },
        sortChanged({ sortBy, sortDesc }) {
            this.fieldSort = { orderNumber: 'order_no', number: 'op_no', name: 'name', statusName: 'statusname', resourceName: 'resource' }[sortBy] || '';
            this.sort = sortDesc ? 'desc' : 'asc'; this.page = 1; this.load();
        },
        toggleHistory(item) {
            if (!item?.operationId) return;
            if (!this.history[item.operationId]) {
                this.$set(this.history, item.operationId, { rows: [], total: 0, page: 1, loading: true, error: '' });
                this.loadHistory(item.operationId);
            }
            this.$set(item, '_showDetails', !item._showDetails);
        },
        async loadHistory(operationId) {
            const state = this.history[operationId];
            state.loading = true; state.error = '';
            try {
                const { data } = await this.$http2.get('/api/mysfc/operation-progress', {
                    params: { siteId: this.siteId, operationId, allPeriod: true, pageNumber: state.page, pageSize: 10 }
                });
                state.rows = data.data.operationsProgress; state.total = data.data.totalItems;
            } catch (_) { state.error = this.t('Error'); }
            finally { state.loading = false; }
        },
        async exportCsv() {
            this.exporting = true; this.error = '';
            try {
                const { data } = await this.$http2.get('/api/mysfc/operation-progress/current/export', { params: this.params() });
                const columns = [
                    ['status', 'Status', 'statusName'], ['order_no', 'OrderNo', 'orderNumber'], ['op_no', 'OpNo', 'number'],
                    ['resource', 'Resource', 'resourceName'], ['produced_quantity', 'QuantityProduced', 'producedQuantity', 'number'],
                    ['rework_quantity', 'QuantityRework', 'rework', 'number'], ['refuse_quantity', 'QuantityRefused', 'refuse', 'number'],
                    ['total_completed', 'TotalCompleted', 'durationInSeconds', 'duration'], ['user', 'User', 'user'],
                    ['event_time', 'EventTime', 'eventTime', 'date'], ['created_at', 'CreatedAt', 'createdAt', 'date'],
                    ['operation_name', 'OperationName', 'name'], ['planned_quantity', 'PlannedQuantity', 'plannedQuantity', 'number'],
                    ['refuse_reason', 'ReasonsRejection', 'refuseReason'], ['updated_at', 'UpdatedAt', 'updatedAt', 'date']
                ];
                const fields = data.data.columns.map(item => columns.find(column => column[0] === item.name)).filter(Boolean);
                const output = [fields.map(column => this.t('Csv.' + column[1]))];
                data.data.rows.forEach(event => output.push(fields.map(([, , key, type]) => {
                    const value = key === 'user' ? this.userLabel(event) : event[key];
                    if (type === 'date') return value ? moment(value).format(this.$cookies.get('userInfo').language === 'pt-BR' ? 'DD-MM-YY HH:mm:ss' : 'MM-DD-YY HH:mm:ss') : '-';
                    if (type === 'duration') return this.duration(value);
                    return value ?? (type === 'number' ? 0 : '-');
                })));
                const csv = output
                    .map(row => row.map(value => {
                        const text = String(value ?? '').replace(/^\s*[=+@-]/, "'$&");
                        return /[",\r\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
                    }).join(';')).join('\n');
                const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
                const link = document.createElement('a'); link.href = url; link.download = `${this.$t('ProductionList.title')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
                link.click(); URL.revokeObjectURL(url);
            } catch (_) { this.error = this.t('Error'); }
            finally { this.exporting = false; }
        }
    }
};
</script>

<style scoped>
.current-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; margin-bottom: 16px; position: relative; z-index: 4; }
.current-heading { flex: 1; min-width: 0; }
.current-title { margin: 0 0 4px; font-weight: 600; font-size: 18px; line-height: 26px; color: #4c4541; }
.current-description { margin: 0; color: #998f8a; font-size: 14px; line-height: 20px; }
.current-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-left: auto; }
.current-actions ::v-deep .btn-title-action { height: 28px; padding: 3px 10px; font-size: 12px; text-decoration: none; white-space: nowrap; }
.current-actions ::v-deep .btn-title-action svg { margin-right: 6px; }
.current-actions ::v-deep .btn-title-action:focus { background-color: #fff !important; }
.current-actions ::v-deep .btn-title-action:disabled { color: #cfc4be !important; border-color: #cfc4be !important; background-color: #fff !important; opacity: 1; }
.current-filters { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; position: relative; z-index: 3; }
.filter-search { flex: 1.4 1 240px; min-width: 200px; }
.filter-select { flex: 1 1 200px; min-width: 180px; }
.current-operations .filter-submit.btn-title-action { height: 2.714rem; min-width: 112px; padding: 6px 21px; font-size: 14px; justify-content: center; text-decoration: none; }
.current-operations .filter-submit.btn-title-action.btn-filled:disabled { background-color: #cfc4be !important; color: #fff !important; border-color: #cfc4be !important; opacity: 1; }
.current-filters ::v-deep .form-group > label { margin-bottom: 4px; line-height: 16px; }
.current-filters ::v-deep .form-control { height: 2.714rem; color: #4c4541; font-size: 14px; }
.current-filters ::v-deep .form-control::placeholder { color: #cfc4be; }
.current-filters ::v-deep .v-select .vs__dropdown-toggle { height: 2.714rem; min-height: 2.714rem; padding: 0 8px 0 0; background: #fff; overflow: hidden; cursor: pointer; }
.current-filters ::v-deep .v-select .vs__search { cursor: pointer; }
.current-filters ::v-deep .v-select .vs__actions { flex-shrink: 0; padding: 0 2px 0 4px; }
.filter-select-arrow { display: flex; align-items: center; }
.filter-select-arrow ::v-deep svg { width: 12px; height: 12px; display: block; }
.filter-period { flex: 0 0 auto; align-self: flex-end; }
.current-filters ::v-deep .filter-period > li .fld__dropdown-button { height: 2.714rem; min-width: 148px; padding: 6px 12px; border-radius: 6px; }
.current-filters ::v-deep .filter-period > li > ul { top: calc(2.714rem + 4px); }
.current-filters ::v-deep .v-select .vs__selected-options { flex-wrap: nowrap; overflow: hidden; }
.current-filters ::v-deep .v-select .vs__selected { display: inline-flex; align-items: center; max-width: calc(100% - 8px); min-width: 0; margin: 4px 2px 4px 6px; }
.filter-selected-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.current-filters ::v-deep .v-select .vs__deselect { flex: 0 0 auto; display: inline-flex; align-items: center; margin-left: 4px; padding: 0; cursor: pointer; }
.current-filters ::v-deep .v-select .vs__search::placeholder { color: #cfc4be; }
.current-filters ::v-deep .v-select .vs__search { margin: 0; height: 2.4rem; }
.current-filters ::v-deep .v-select.vs--open .vs__dropdown-toggle { border-color: #974900; box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.15); }
.pagination-table ::v-deep .custom-select { width: 80px; }
.current-operations ::v-deep .form-control:focus { border-color: #974900; box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.15); }
.current-operations ::v-deep label,
.current-operations ::v-deep legend { color: #4c4541; font-size: 12px; font-weight: 600; }
.current-operations ::v-deep .spinner-border { color: #974900; }
.history-panel { max-width: calc(100vw - 170px); padding: 16px; background: #faf6f2; border-left: 3px solid #974900; border-radius: 0 6px 6px 0; }
.status-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 7px; vertical-align: middle; }
.summary-value { display: block; max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.operation-summary ::v-deep .badge-status-custom { max-width: 180px; color: #fff; font-weight: 600; }
.btn-expand { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 10px; border-radius: 6px; border: 1px solid #974900; background: #fff; color: #974900; cursor: pointer; font-size: 13px; font-weight: 600; white-space: nowrap; }
.btn-expand:hover { background: #ffede1; }
.btn-expand.is-open { background: #974900; color: #fff; }
.btn-expand.is-open:hover { background: #8a4300; }
.table-production-progress ::v-deep > table > tbody > tr:not(.b-table-details) { cursor: pointer; }
.table-production-progress ::v-deep > table > tbody > tr:not(.b-table-details):hover { background-color: #ffede1; }
.table-production-progress ::v-deep > table > tbody > tr.b-table-details > td { padding: 8px 0 8px 10px !important; background: #fff; }
.operation-summary ::v-deep th { background-color: rgba(151, 73, 0, 0.05) !important; color: #4c4541 !important; font-weight: 600; font-size: 12px; line-height: 16px; text-transform: uppercase; border-top: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0; padding: 10px !important; vertical-align: middle; white-space: normal; }
.operation-summary ::v-deep td { color: #4c4541; font-size: 14px; line-height: 20px; font-weight: 400; border-top: none; border-bottom: 1px solid #e0e0e0; padding: 10px !important; height: 58px; vertical-align: middle; background: transparent; }
.operation-summary ::v-deep tbody > tr:nth-child(odd) { background-color: #fafafa; }
.operation-summary ::v-deep tbody > tr:nth-child(even) { background-color: #fff; }
.operation-summary ::v-deep .b-table-details { background-color: #fff !important; }
.operation-summary ::v-deep .b-table-empty-row td { height: 120px; text-align: center; color: #998f8a; font-weight: 600; }
.history-table ::v-deep td { height: 44px; }
.current-operations ::v-deep .pagination .page-item.active .page-link { background-color: #974900 !important; border-color: #974900 !important; color: #fff !important; }
.current-operations ::v-deep .pagination .page-item .page-link { color: #4c4541; }
.current-operations ::v-deep .pagination .page-item:not(.active) .page-link:hover { color: #974900; }
.current-operations ::v-deep .pagination .page-item.disabled .page-link { background-color: #faf6f2; color: #4c4541; }
.current-operations .text-muted { color: #998f8a !important; }
@media (max-width: 767px) {
    .current-toolbar { flex-direction: column; align-items: stretch; }
    .current-actions { margin-left: 0; width: 100%; }
    .current-filters > .form-group,
    .filter-period,
    .filter-submit { flex: 1 1 100%; width: 100%; min-width: 0; }
    .current-filters ::v-deep .filter-period,
    .current-filters ::v-deep .filter-period > li,
    .current-filters ::v-deep .filter-period .fld__dropdown-button { width: 100%; }
    .history-panel { max-width: calc(100vw - 70px); padding: 12px; }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
